import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function DoncasterCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Doncaster Pothole Claims | 32,000 Repairs in Five Years | Fixtyer"
        description="City of Doncaster Council earns GREEN DfT spend scorecards but stays AMBER overall — 13% of its 1,150km of residential roads remain in RED condition and it filled 32,092 potholes in five years. See what the data means for your claim."
        canonical="/councils/doncaster"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Doncaster', url: '/councils/doncaster' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Doncaster has a GREEN spend scorecard — can I still claim for pothole damage?',
            answer: 'Yes. The DfT Spend scorecard is GREEN because Doncaster typically spends at or above its DfT capital allocation and dedicates 85–86% of funding to preventative works. But the overall rating is AMBER because road condition is AMBER — B and C roads in RED condition doubled from 2% to 4% between 2021 and 2024, and 13% of unclassified roads remain in RED condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road in Doncaster?',
            answer: 'U-roads make up 1,150km — 71% of Doncaster\'s 1,622km network. At the last survey in 2024, 13% were in RED condition — roughly 150km of estate streets, village routes and residential roads. The council surveys unclassified roads by Annual Engineering Inspection (AEI), with north and south halves of the city surveyed in alternate years on a two-year cycle. If your road was in the half not surveyed that year, network-level condition data may not exist for it.',
          },
          {
            question: 'Doncaster filled fewer potholes in 2022/23 — does that mean the roads were in good shape?',
            answer: 'No. Pothole repairs dropped to 4,315 in 2022/23 — the lowest figure in five years — before rising again to 6,262 in 2023/24 and 5,877 in 2024/25. The council itself notes the figure "will be affected by external factors, such as the severity of the weather in winter, or other events." A low repair year does not mean fewer defects formed; it may reflect weather, staffing, or reporting variation.',
          },
          {
            question: 'Why did B and C roads in RED condition double in Doncaster?',
            answer: 'Doncaster\'s own SCANNER data shows B and C roads in RED condition rose from 2% in 2021 and 2022 to 4% in 2023 and 2024 — while good-condition B/C roads fell from 76% to 73%. That is 12–13km of classified local roads now flagged for maintenance on a 309km B/C network, at a time when carriageway resurfacing fell from 31.96km in 2022/23 to 8.54km in 2023/24.',
          },
          {
            question: 'Does Doncaster\'s alternate-year U-road survey help or hurt my claim?',
            answer: 'It can help. The council surveys unclassified roads by AEI with north and south halves of the city alternating each year — meaning any given residential area is condition-surveyed roughly once every two years, not annually. The council describes this as an improvement on the DfT minimum four-year cycle, but for Section 58 the question is whether that frequency was reasonable for the specific road where your defect formed — especially when 13% of U-roads were already in RED condition at the last survey.',
          },
        ]}
      />
      <ServiceSchema
        councilName="City of Doncaster Council"
        councilSlug="doncaster"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/doncaster"
        headline="Doncaster Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Doncaster</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Doncaster: 32,000 Potholes, 13% of Residential Roads in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            City of Doncaster Council earns a <strong className="text-emerald-400">GREEN DfT Spend scorecard</strong> and
            dedicates 86% of funding to preventative maintenance. Yet the overall rating is{' '}
            <strong className="text-amber-400">AMBER</strong> — because{' '}
            <strong className="text-rose-400">13% of its 1,150km of residential roads remain in RED condition</strong>,
            B/C roads in RED condition doubled since 2021, and the council still filled{' '}
            <strong className="text-rose-400">32,092 potholes in five years</strong>.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">13%</div>
            <div className="text-white font-medium mt-2">Of U-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              1,150km of unclassified roads — 71% of Doncaster's entire network — with roughly 150km
              flagged for maintenance at the last AEI survey, down from 23% in 2020 but still one in eight
              residential routes.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of SCANNER and AEI survey data from Doncaster's own transparency report — classified
            roads slipping while residential routes improve from a worse baseline
          </p>

          {/* Network overview */}
          <div className="grid sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">163km</div>
              <div className="text-xs text-surface-600">A roads (10%)</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-brand-900">309km</div>
              <div className="text-xs text-surface-600">B and C roads (19%)</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center">
              <div className="text-2xl font-bold text-amber-700">1,150km</div>
              <div className="text-xs text-amber-800">U roads (71%)</div>
            </div>
            <div className="bg-brand-950 text-white rounded-xl p-4 text-center">
              <div className="text-2xl font-bold">1,622km</div>
              <div className="text-xs text-brand-300">Total network</div>
            </div>
          </div>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (163km — 10% of network): slowly declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">24%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">3%</td>
                    <td className="px-4 py-2 text-center font-bold">25%</td>
                    <td className="px-4 py-2 text-center font-bold">72%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED condition rose from 2% to 3% and good-condition roads fell from 74% to 72%.
              SCANNER surveys cover 100% of classified roads every year — so the council has no excuse
              for not knowing A-road condition.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (309km — 19% of network): RED condition doubled</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">21%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">23%</td>
                    <td className="px-4 py-2 text-center">75%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">2%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">76%</td>
                  </tr>
                  <tr className="border-b border-amber-100 bg-rose-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center">22%</td>
                    <td className="px-4 py-2 text-center">74%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">4%</td>
                    <td className="px-4 py-2 text-center font-bold">23%</td>
                    <td className="px-4 py-2 text-center font-bold">73%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              B/C roads in RED condition doubled from 2% to 4% between 2021 and 2023 — and stayed there.
              Good-condition B/C roads fell from 76% to 73%. Roughly 27% of the B/C network now needs or
              will soon need maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">And This Is The Well-Funded Version</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.3m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£5.8m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">86%</div>
                <div className="text-brand-300 text-sm">Preventative share (2024/25)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Doncaster typically spends at or above its DfT allocation — peaking at{' '}
              <span className="text-emerald-400">£14.3m capital in 2021/22</span> against £12.3m allocated —
              and classifies 85–86% of spend as preventative. The DfT Spend scorecard is GREEN. Yet classified
              roads are still slipping and 32,092 potholes were filled in five years. The problem is not
              the chequebook alone — it is whether the specific road you drove was maintained in time.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 1,150km Residential Network
          </h2>
          <p className="text-surface-600 mb-8">
            71% of Doncaster's roads are unclassified — surveyed by AEI on an alternate-year north/south cycle
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100 bg-rose-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">23%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">17%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">13%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">13%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="These are determined by AEI for the Unclassified roads (North of city/South of city alternates each year as a 2 year cycle. This regime is an improvement on the recommended DfT minimum 4 year cycle standard, however this does help the City of Doncaster Council understand condition and priority of maintenance requirements in a more robust manner."
              source="City of Doncaster Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Progress — But From A Worse Starting Point</h3>
              <p className="text-surface-600 text-sm mb-3">
                U-road RED condition has fallen from 23% in 2020 to 13% in 2024 — a genuine improvement.
                But 13% of 1,150km still means roughly <strong>150km of residential roads</strong> flagged
                for maintenance at the last survey. That is one in eight unclassified routes.
              </p>
              <p className="text-surface-600 text-sm">
                Doncaster publishes RED percentages only for U-roads — not amber or green splits — so
                the full condition picture on residential streets is less transparent than on classified roads.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Alternate-Year Survey Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                North and south halves of the city alternate each year — so any given residential area
                gets an AEI condition survey roughly once every two years, not annually. The council
                describes this as better than the DfT's four-year minimum, but it still means 12 months
                of blind spots on half the U-road network at any time.
              </p>
              <p className="text-amber-800 text-sm">
                From 2026/27, PAS 2161 will replace the current three-category system with five categories —
                Doncaster says it will continue using SCANNER and AEIs to deliver the new standard.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence under Section 41 of the Highways Act 1980, Doncaster must
              show it had a reasonable system for knowing the condition of its roads. For the 1,150km
              unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Was your road in the north or south half surveyed in the year of your incident?</li>
              <li>• If 13% of U-roads were RED at the last survey, what was done about yours specifically?</li>
              <li>• How does the council track deterioration on a road it condition-surveys only every other year?</li>
              <li>• Does the council's own data show your road class was already flagged before your damage occurred?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of a residential network it only measures in
              alternate years — then expect that gap to protect it from every claim.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            32,092 Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Reactive repair volumes from Doncaster's own transparency report — averaging 6,418 per year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled (estimate)</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">7,702</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">7,936</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">4,315</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">6,262</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center">5,877</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">32,092</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Over the past five years, the estimated number of potholes repaired has varied from 7936 (2021/22) to 4315 (2022/23), highlighted in table 2.3 below, with an average of approximately 6418 per year."
              source="City of Doncaster Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~18 Potholes a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Doncaster fills around 6,418 potholes annually — roughly 18 per day.
                For 2025/26 the council projects 6,416 repairs based on the five-year average, but notes this
                "will be affected by external factors, such as the severity of the weather in winter, or other events."
                A network producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Preventative Spend Has Not Eliminated Potholes</h3>
              <p className="text-amber-800 text-sm">
                Reactive maintenance accounted for 14% of total spend in 2024/25 — yet still required nearly
                5,877 pothole repairs that year. Preventative maintenance covered 46.37km of carriageway in
                2024/25 on a 1,622km network. That is less than 3% of the network receiving capital treatment
                in a single year — the rest relies on inspections catching defects before they damage vehicles.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Resurfacing roller coaster */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Resurfacing Roller Coaster
          </h2>
          <p className="text-surface-600 mb-8">
            Maintenance activity data shows capital treatment volumes swinging sharply year to year
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Activity</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2020/21</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2021/22</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2022/23</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2023/24</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">2024/25</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium">Carriageway resurfaced (km)</td>
                  <td className="px-4 py-3 text-center">7.73</td>
                  <td className="px-4 py-3 text-center">31.14</td>
                  <td className="px-4 py-3 text-center">31.96</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">8.54</td>
                  <td className="px-4 py-3 text-center">11.87</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium">Surface treatments (km)</td>
                  <td className="px-4 py-3 text-center">29.71</td>
                  <td className="px-4 py-3 text-center">26.34</td>
                  <td className="px-4 py-3 text-center">39.21</td>
                  <td className="px-4 py-3 text-center">26.47</td>
                  <td className="px-4 py-3 text-center">32.00</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-medium">Carriageway patching (sq.m)</td>
                  <td className="px-4 py-3 text-center">10,795</td>
                  <td className="px-4 py-3 text-center">20,194</td>
                  <td className="px-4 py-3 text-center">26,559</td>
                  <td className="px-4 py-3 text-center">11,829</td>
                  <td className="px-4 py-3 text-center">16,639</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-medium">Footways improved (sq.m)</td>
                  <td className="px-4 py-3 text-center">61,796</td>
                  <td className="px-4 py-3 text-center">160,916</td>
                  <td className="px-4 py-3 text-center">57,251</td>
                  <td className="px-4 py-3 text-center">72,392</td>
                  <td className="px-4 py-3 text-center">53,378</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">The 2023/24 Resurfacing Collapse</h3>
              <p className="text-surface-600 text-sm mb-3">
                Carriageway resurfacing fell from 31.96km in 2022/23 to just 8.54km in 2023/24 — a 73% drop —
                before recovering modestly to 11.87km in 2024/25. That same period saw B/C roads in RED
                condition jump to 4% and stay there.
              </p>
              <p className="text-surface-600 text-sm">
                For 2025/26 Doncaster plans 6.3km of resurfacing and 47km of surface treatments — locations
                "to be confirmed following assessments and approvals."
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Spending Table Context</h3>
              <p className="text-amber-800 text-sm mb-3">
                DfT capital allocation swung from £12.3m (2021/22) to £3.5m (2023/24) before recovering to
                £5.1m (2024/25). Total capital spend tracked higher in most years — £14.3m, £9.7m, £4.7m,
                £6.0m — with revenue spend rising steadily from £780k to £974k.
              </p>
              <p className="text-amber-800 text-sm">
                Reactive maintenance's revenue share peaked at 17% in 2023/24 — the same year resurfacing
                collapsed and pothole repairs climbed back to 6,262.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="We continue to prioritise preventative maintenance, with 85% of our funding dedicated to resurfacing, reconstruction, and various surface treatments in 2025/26. Our aim is to improve this by increased capital funding in 2026/27 to improve the network for the public, prolong asset life and reduce emergency reactive repair costs"
            source="City of Doncaster Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Inspection methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            How Doncaster Inspects Its Roads
          </h2>
          <p className="text-surface-600 mb-8">
            SCANNER for classified roads, AEI for unclassified — with a methodology change coming in 2026/27
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Classified Roads (472km)</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• A, B and C roads surveyed by SCANNER laser technology</li>
                <li>• 100% of classified roads surveyed every year</li>
                <li>• Three condition categories: Green, Amber, Red</li>
                <li>• RED means "Should be considered for maintenance"</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Unclassified Roads (1,150km)</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Surveyed by Annual Engineering Inspection (AEI)</li>
                <li>• North/south halves alternate each year — two-year cycle per area</li>
                <li>• RED percentage published; amber/green splits not reported</li>
                <li>• Also subject to routine scheduled highway safety inspections</li>
              </ul>
            </div>
          </div>

          <QuoteCard
            quote="The balance between preventative and reactive maintenance is determined using a combination of condition data (e.g. SCANNER, Annual Engineering Inspection, visual survey, routine scheduled highway safety inspections), annual performance reviews, and local member and resident feedback."
            source="City of Doncaster Council Local Highways Maintenance Transparency Report 2025"
          />

          <div className="mt-8 bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The PAS 2161 Change Ahead</h3>
            <p className="text-brand-200 mb-4">
              Doncaster's report notes that from 2026/27 a new BSI PAS 2161 standard will replace the current
              three-category system with five categories. The council states it will continue using a combination
              of SCANNER and AEIs to deliver to the new standard.
            </p>
            <p className="text-white font-medium">
              If your incident predates the methodology change, the council's own historical condition records
              under the current RED/amber/green system are what matter for your claim — not future categories.
            </p>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a GREEN-Spend AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Doncaster invests seriously — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend typically matches or exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> 85–86% of funding classed as preventative maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED condition improved from 23% to 13% since 2020
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> SCANNER surveys 100% of classified roads annually
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management strategy aligned to Well-Managed Highway Infrastructure
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence. Generic "the roads are terrible" claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — B/C RED doubled from 2% to 4% since 2021
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 13% of 1,150km U-roads still in RED — roughly 150km of residential routes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> U-roads surveyed only every other year by AEI — alternate north/south halves
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 32,092 potholes filled in five years — defects form faster than capital treatment covers the network
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Resurfacing collapsed to 8.54km in 2023/24 as B/C RED condition jumped to 4%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER best practice scorecard — not the top tier nationally
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend scorecards and an 86% preventative programme, your claim lives
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the alternate-year AEI survey gap is your strongest structural argument</li>
              <li>• Timing relative to the 2023/24 resurfacing collapse when B/C RED condition jumped</li>
              <li>• Whether your road was in the north or south half last condition-surveyed</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Doncaster's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Doncaster?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road AEI survey-gap argument</li>
                <li>• No prior-report search</li>
                <li>• No B/C deterioration timeline</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ B/C RED doubling documented</li>
                <li>✅ Alternate-year U-road survey argued</li>
                <li>✅ 32,092 potholes in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Doncaster</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=doncaster"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Doncaster Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Doncaster has a GREEN spend scorecard — can I still claim for pothole damage?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN because Doncaster typically spends at or above its DfT
                capital allocation and dedicates 85–86% of funding to preventative works. But the overall rating
                is AMBER because road condition is AMBER — B and C roads in RED condition doubled from 2% to 4%
                between 2021 and 2024, and 13% of unclassified roads remain in RED condition. Section 58 turns on
                whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on
                aggregate spend.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road in Doncaster?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 1,150km — 71% of Doncaster's 1,622km network. At the last survey in 2024, 13%
                were in RED condition — roughly 150km of estate streets, village routes and residential roads. The
                council surveys unclassified roads by Annual Engineering Inspection (AEI), with north and south
                halves of the city surveyed in alternate years on a two-year cycle. If your road was in the half
                not surveyed that year, network-level condition data may not exist for it.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Doncaster filled fewer potholes in 2022/23 — does that mean the roads were in good shape?
              </h3>
              <p className="text-surface-600 text-sm">
                No. Pothole repairs dropped to 4,315 in 2022/23 — the lowest figure in five years — before rising
                again to 6,262 in 2023/24 and 5,877 in 2024/25. The council itself notes the figure "will be affected
                by external factors, such as the severity of the weather in winter, or other events." A low repair
                year does not mean fewer defects formed; it may reflect weather, staffing, or reporting variation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why did B and C roads in RED condition double in Doncaster?
              </h3>
              <p className="text-surface-600 text-sm">
                Doncaster's own SCANNER data shows B and C roads in RED condition rose from 2% in 2021 and 2022 to
                4% in 2023 and 2024 — while good-condition B/C roads fell from 76% to 73%. That is 12–13km of
                classified local roads now flagged for maintenance on a 309km B/C network, at a time when carriageway
                resurfacing fell from 31.96km in 2022/23 to 8.54km in 2023/24.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does Doncaster's alternate-year U-road survey help or hurt my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can help. The council surveys unclassified roads by AEI with north and south halves of the city
                alternating each year — meaning any given residential area is condition-surveyed roughly once every
                two years, not annually. The council describes this as an improvement on the DfT minimum four-year
                cycle, but for Section 58 the question is whether that frequency was reasonable for the specific road
                where your defect formed — especially when 13% of U-roads were already in RED condition at the last survey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.doncaster.gov.uk/services/transport-streets-parking/annual-local-highway-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              City of Doncaster Council Local Highways Maintenance Transparency Report (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
